// src/components/reports/TaskSummaryCard.js
import React from "react";
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  ListChecks,
} from "lucide-react";

/**
 * TaskSummaryCard
 *
 * Props:
 * - tasks: {
 *     total: number,
 *     completed: number,
 *     pending: number,
 *     overdue: number
 *   }
 *
 * NOTE:
 * - This component expects AGGREGATED task data
 * - Source of truth: GET /reports/project/:projectId
 */
export default function TaskSummaryCard({ tasks }) {
  if (!tasks) {
    return (
      <div className="rounded-xl border bg-white p-6 text-sm text-gray-500">
        No task data available.
      </div>
    );
  }

  const {
    total = 0,
    completed = 0,
    pending = 0,
    overdue = 0,
  } = tasks;

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-3">
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="text-lg font-semibold text-gray-900">
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border bg-white p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Task Summary
        </h2>
        <span className="text-xs text-gray-500">
          Aggregated
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat
          icon={ListChecks}
          label="Total"
          value={total}
          color="bg-gray-100 text-gray-700"
        />

        <Stat
          icon={CheckCircle}
          label="Completed"
          value={completed}
          color="bg-emerald-50 text-emerald-600"
        />

        <Stat
          icon={Clock}
          label="Pending"
          value={pending}
          color="bg-amber-50 text-amber-600"
        />

        <Stat
          icon={AlertTriangle}
          label="Overdue"
          value={overdue}
          color="bg-rose-50 text-rose-600"
        />
      </div>
    </div>
  );
}